import type { InventoryFilters } from '@/lib/api/client/inventory'
import type { InventoryIncludeFacet } from '@/lib/api/schemas/inventory.schema'
import { ORDER_GROUP_DEFAULT_VISIBLE_COLUMNS, INVENTORY_COLUMN_REGISTRY, type InventoryColumnFacet, type InventoryColumnId } from '@/lib/views/columns/inventory'
import type { ViewPreset, ViewPresetKey } from '@/lib/views/presets'

export type InventoryViewPreset = ViewPreset<'inventory', InventoryColumnId, InventoryFilters>

export const INVENTORY_VIEW_PRESETS: readonly InventoryViewPreset[] = [
  {
    key: 'actual',
    label: 'Актуальное',
    grain: 'inventory',
    columns: [
      'photo',
      'title',
      'attributes',
      'size',
      'weight',
      'article',
      'uin',
      'arrivedAt',
      'status',
      'activity',
      'alerts',
      'problems',
      'photoCount',
      'videoCount',
      'marketplaces',
    ],
    sort: { column: 'activity', direction: 'descending' },
    filters: { localStock: 'in_stock' },
    actions: [],
  },
  {
    key: 'platforms',
    label: 'Площадки',
    grain: 'inventory',
    columns: ['photo', 'product', 'size', 'weight', 'status', 'storefronts', 'onecPrice', 'wildberriesPrice', 'ymarketPrice', 'ozonPrice'],
    sort: { column: 'article', direction: 'ascending' },
    filters: { localStock: 'in_stock' },
    actions: ['publish', 'reprice'],
    headerButtons: ['cardSettings', 'ymarketCollect'],
  },
  {
    key: 'ymarket_cheap',
    label: 'Маркет: ниже порога',
    grain: 'inventory',
    columns: ['photo', 'product', 'size', 'weight', 'status', 'storefronts', 'onecPrice', 'wildberriesPrice', 'ymarketPrice', 'ymarketShowcasePerGram', 'ymarketShowcaseThresholdRatio', 'ozonPrice'],
    sort: { column: 'ymarketShowcaseThresholdRatio', direction: 'ascending' },
    filters: { localStock: 'in_stock', ymarketBelowThreshold: true },
    actions: ['publish', 'reprice'],
    headerButtons: ['cardSettings', 'ymarketCollect'],
  },
  {
    key: 'orders',
    label: 'Заказы',
    grain: 'inventory',
    columns: ORDER_GROUP_DEFAULT_VISIBLE_COLUMNS,
    sort: { column: 'orderCreatedAt', direction: 'descending' },
    filters: {},
    actions: [],
  },
]

const FACET_ORDER: readonly InventoryIncludeFacet[] = ['storefrontPrices', 'presence', 'readiness', 'ymarketEconomics']

export function inventoryIncludeForColumns(columns: readonly InventoryColumnId[]): InventoryIncludeFacet[] {
  const selected = new Set<InventoryColumnFacet>()
  for (const id of columns) {
    const column = INVENTORY_COLUMN_REGISTRY.find((candidate) => candidate.id === id)
    for (const facet of column?.facets ?? []) selected.add(facet)
  }
  return FACET_ORDER.filter((facet) => selected.has(facet))
}

export function inventoryPreset(key: ViewPresetKey): InventoryViewPreset {
  const preset = INVENTORY_VIEW_PRESETS.find((candidate) => candidate.key === key)
  if (!preset) throw new Error(`Unknown inventory preset: ${key}`)
  return preset
}

export function ordersPreset(): InventoryViewPreset {
  return inventoryPreset('orders')
}
